import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";

const AuthContext = createContext(null);
const TOKEN_KEY = "session_token";

// sessionStorage scopes the token to the current tab and is wiped on tab close.
// localStorage is XSS-readable across all scripts and persists indefinitely.
const tokenStore = {
  get: () => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      console.error("Failed to persist session token:", e);
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      // Best-effort cleanup of legacy localStorage tokens
      localStorage.removeItem(TOKEN_KEY);
    } catch (err) {
      console.warn("Token cleanup failed:", err);
    }
  },
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Keep token in a ref so callers calling getToken() always read the latest
  // value without having to subscribe to re-renders.
  const tokenRef = useRef(tokenStore.get());

  const getToken = useCallback(() => tokenRef.current, []);
  const authHeaders = useCallback(() => {
    const t = tokenRef.current;
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const setToken = useCallback((token) => {
    tokenRef.current = token;
    if (token) tokenStore.set(token);
    else tokenStore.clear();
  }, []);

  const checkAuth = useCallback(async () => {
    const token = tokenRef.current;
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error("Auth check failed:", error);
        setToken(null);
      }
    }
    setLoading(false);
  }, [setToken]);

  useEffect(() => {
    // If returning from OAuth callback, AuthCallback will establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // One-time migration: if a legacy token is sitting in localStorage, port it
  // over to sessionStorage and re-run auth check so the user lands logged in.
  useEffect(() => {
    try {
      const legacy = localStorage.getItem(TOKEN_KEY);
      if (legacy) {
        if (!tokenRef.current) {
          tokenStore.set(legacy);
          tokenRef.current = legacy;
          // Re-run the auth check now that we have a token to validate
          checkAuth();
        }
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch (err) {
      console.warn("Legacy token migration skipped:", err);
    }
  }, [checkAuth]);

  const login = useCallback(async (sessionId) => {
    try {
      // Include referral attribution captured from ?ref= links (if any)
      const refCode = sessionStorage.getItem("ref_code");
      const payload = { session_id: sessionId, ...(refCode ? { ref_code: refCode } : {}) };
      const response = await axios.post(`${API}/auth/session`, payload);
      sessionStorage.removeItem("ref_code");
      setToken(response.data.session_token);
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }, [setToken]);

  const loginWithPassword = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.session_token);
    setUser(response.data.user);
    return response.data.user;
  }, [setToken]);

  const register = useCallback(async (name, email, password) => {
    const refCode = sessionStorage.getItem("ref_code");
    const payload = { name, email, password, ...(refCode ? { ref_code: refCode } : {}) };
    const response = await axios.post(`${API}/auth/register`, payload);
    sessionStorage.removeItem("ref_code");
    setToken(response.data.session_token);
    setUser(response.data.user);
    return response.data.user;
  }, [setToken]);

  const logout = useCallback(async () => {
    const token = tokenRef.current;
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
    setToken(null);
    setUser(null);
  }, [setToken]);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    loading,
    login,
    loginWithPassword,
    register,
    logout,
    refreshUser,
    getToken,
    authHeaders,
    isAuthenticated: !!user,
    isAdmin: !!user?.is_admin,
    hasSubscription: user?.subscription_status === "subscribed",
    predictionCredits: user?.prediction_credits || 0,
    hasHotPicksAccess: user?.hot_picks_access || user?.subscription_status === "subscribed",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
